import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, FileText, Database, Zap, MapPin, Lightbulb, BarChart3, FileCheck,
  Plus, Camera, CheckCircle, AlertTriangle, AlertCircle, TrendingUp, DollarSign, Download,
  Wifi, WifiOff, ClipboardCheck, Calculator, Target, Leaf, Droplets, Trash2,
  Info, ChevronDown, ChevronRight, Sparkles, FileDown, Settings, RefreshCw, Flame,
  Activity, Clock, Printer
} from 'lucide-react';
import TabNavigation from '../../components/TabNavigation';
import DesktopAssessment from './DesktopAssessment';
import OpportunityEngine from './OpportunityEngine';
import OpportunityEngineV2 from './OpportunityEngineV2';
import OpportunityEngineV3 from './OpportunityEngineV3';
import Scope3Module from './Scope3Module';
import FinalReport from './FinalReport';
import SEUModule from './SEUModule';
import OnSiteStudySampleReport from './OnSiteStudySampleReport';
import ERSTrends from './ERSTrends';
import { generateBrandedPdf, fetchTenantLogoDataUrl, getCurrentTenantName } from '../../utils/pdfBranding';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import { formatDate, formatDateTime, formatReportDate, formatTimeAgo } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

function ERSMain({ user, onLogout }) {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const { activeSiteId, initialized: siteInitialized } = useSite();
  // Snapshot of the site we entered this study with. Once SiteContext has
  // initialised, the FIRST observation is captured; any later change to
  // activeSiteId means the user picked a different site from the header,
  // which makes the current study's context stale — bounce them to the
  // Studies list so they pick a study under the new site.
  const initialSiteRef = useRef(undefined);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [study, setStudy] = useState(null);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Progress states for all sections
  const [progressData, setProgressData] = useState({
    desktop: { completion: 0, lastUpdated: 'Not started', extra: null },
    seu: { completion: 0, lastUpdated: 'Not started', extra: null },
    onsite: { completion: 0, lastUpdated: 'Not started', extra: null },
    opportunity: { completion: 0, lastUpdated: 'Not started', extra: null },
    scope3: { completion: 0, lastUpdated: 'Not started', extra: null },
    report: { completion: 0, lastUpdated: 'Not started', extra: null }
  });
  
  // AI Insights state
  const [aiInsights, setAiInsights] = useState([]);

  useEffect(() => {
    fetchStudyDetails();
  }, [studyId]);

  // Redirect to Studies list when the user picks a different site from the
  // header. Wait for SiteContext to finish initialising before capturing the
  // baseline so the localStorage restore on mount isn't treated as a change.
  useEffect(() => {
    if (!siteInitialized) return;
    if (initialSiteRef.current === undefined) {
      initialSiteRef.current = activeSiteId ?? null;
      return;
    }
    if ((activeSiteId ?? null) !== initialSiteRef.current) {
      navigate('/ers/studies');
    }
  }, [siteInitialized, activeSiteId, navigate]);

  useEffect(() => {
    // Update URL when tab changes
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab]);

  useEffect(() => {
    // Sync tab state from URL when searchParams change (e.g., via navigate())
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);
  
  // Helper to calculate time ago
  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Not started';
    return formatTimeAgo(dateString);
  };

  const fetchStudyDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const studyRes = await axios.get(`${API}/api/ers/studies/${studyId}`, config);
      setStudy(studyRes.data);
      
      if (studyRes.data.site_id) {
        const siteRes = await axios.get(`${API}/api/ers/sites/${studyRes.data.site_id}`, config);
        setSite(siteRes.data);
      }
      
      // Fetch all progress data in parallel
      const newProgress = { ...progressData };
      const insights = [];
      
      // 1. Desktop Analysis Progress - Check SEU summary for resource data
      try {
        const seuSummaryRes = await axios.get(`${API}/api/ers/seu/summary/${studyId}`, config);
        const seuData = seuSummaryRes.data;
        
        if (seuData) {
          const totals = seuData.totals_by_category || {};
          const hasElectricity = (totals.electrical_kwh || 0) > 0;
          const hasFuel = (totals.fuel_gj || 0) > 0;
          const hasWater = (totals.water_kl || 0) > 0;
          const hasWaste = (totals.waste_mt || 0) > 0;
          
          const completedTypes = [hasElectricity, hasFuel, hasWater, hasWaste].filter(Boolean).length;
          const desktopCompletion = Math.round((completedTypes / 4) * 100);
          
          const missingTypes = [];
          if (!hasElectricity) missingTypes.push('Electricity');
          if (!hasFuel) missingTypes.push('Fuel');
          if (!hasWater) missingTypes.push('Water');
          if (!hasWaste) missingTypes.push('Waste');
          
          newProgress.desktop = {
            completion: desktopCompletion,
            lastUpdated: seuData.calculated_at ? getTimeAgo(seuData.calculated_at) : 'Recently',
            extra: `${completedTypes}/4 resource types complete`
          };
          
          // AI Insight for desktop
          if (missingTypes.length > 0 && missingTypes.length < 4) {
            insights.push({
              type: 'action',
              text: `Complete ${missingTypes.join(', ')} data in Desktop Study to improve analysis accuracy.`
            });
          }
          
          // 2. SEU Identification Progress - Also from the same summary
          const seuCounts = seuData.seu_counts || {};
          const totalSEUs = seuCounts.total || 0;
          
          if (totalSEUs > 0) {
            const coverage = seuData.seu_coverage_percent?.overall_energy || 0;
            const seuCompletion = Math.min(Math.round(coverage), 100);
            
            newProgress.seu = {
              completion: seuCompletion > 0 ? Math.max(seuCompletion, 50) : 0,
              lastUpdated: getTimeAgo(seuData.calculated_at),
              extra: `${totalSEUs} SEUs identified (${seuData.meets_80_percent_threshold ? '80% threshold met' : 'Below 80% threshold'})`
            };
            
            // AI insight for top SEU
            if (seuData.top_3_seus && seuData.top_3_seus.length > 0) {
              const topSEU = seuData.top_3_seus[0];
              insights.push({
                type: 'insight',
                text: `${topSEU.name} is your largest SEU (${topSEU.share?.toFixed(1)}% of ${topSEU.category}). Prioritize efficiency improvements here.`
              });
            }
          }
        }
      } catch (e) {
        console.log('No SEU summary data yet:', e.message);
      }
      
      // 3. On-Site Assessment Progress
      try {
        const sessionsRes = await axios.get(`${API}/api/ers/onsite/sessions/${studyId}`, config);
        const sessions = sessionsRes.data || [];
        const totalSessions = sessions.length;
        const completedSessions = sessions.filter(s => s.status === 'Closed').length;
        
        if (totalSessions > 0) {
          const onsiteCompletion = Math.round((completedSessions / totalSessions) * 100);
          const lastSession = sessions.sort((a, b) => 
            new Date(b.measurement_date) - new Date(a.measurement_date)
          )[0];
          
          newProgress.onsite = {
            completion: onsiteCompletion,
            lastUpdated: lastSession ? getTimeAgo(lastSession.measurement_date) : 'Not started',
            extra: `${completedSessions}/${totalSessions} sessions complete`
          };
          
          // AI insight for on-site
          if (completedSessions < totalSessions) {
            insights.push({
              type: 'action',
              text: `${totalSessions - completedSessions} measurement session(s) still in progress. Complete them to finalize on-site data.`
            });
          }
        }
      } catch (e) {
        console.log('No on-site sessions yet');
      }
      
      // 4. Opportunity Engine Progress - Check if opportunities identified
      try {
        const oppsRes = await axios.get(`${API}/api/ers/opportunities/${studyId}`, config);
        const opportunities = oppsRes.data || [];
        const totalOpps = opportunities.length;
        
        if (totalOpps > 0) {
          // If opportunities exist, they are complete (auto-generated)
          newProgress.opportunity = {
            completion: 100,
            lastUpdated: getTimeAgo(opportunities[0]?.created_at),
            extra: `${totalOpps} opportunities identified`
          };
          
          // AI insight for savings
          const totalSavings = opportunities.reduce((sum, o) => sum + (o.annual_cost_savings || o.annual_savings || 0), 0);
          if (totalSavings > 0) {
            insights.push({
              type: 'opportunity',
              text: `Potential annual savings of ₹${(totalSavings / 100000).toFixed(1)} Lakhs identified across ${totalOpps} opportunities.`
            });
          }
        }
      } catch (e) {
        console.log('No opportunities yet');
        // Check if we have enough data to start opportunity engine
        if (newProgress.seu.completion > 50 && newProgress.onsite.completion > 0) {
          insights.push({
            type: 'action',
            text: 'Run AI Analysis in Opportunity Engine to identify energy saving opportunities.'
          });
        }
      }
      
      // 5. Scope 3 Progress - Check if any categories have data
      if (studyId) {
        try {
          const scope3Res = await axios.get(`${API}/api/scope3/hotspot/${studyId}`, config);
          const scope3Data = scope3Res.data;
          
          console.log('Scope 3 data:', scope3Data); // Debug log
          
          if (scope3Data && scope3Data.total_scope3_emissions > 0) {
            const categoriesWithData = scope3Data.categories.filter(c => c.emissions_tco2e > 0).length;
            const completionPct = Math.round((categoriesWithData / 5) * 100); // 5 categories
            
            newProgress.scope3 = {
              completion: completionPct,
              lastUpdated: formatDate(new Date()),
              extra: `${categoriesWithData}/5 categories completed • ${scope3Data.total_scope3_emissions.toFixed(1)} tCO₂e`
            };
            
            if (categoriesWithData >= 3) {
              insights.push({
                type: 'success',
                text: `Scope 3 screening ${categoriesWithData >= 5 ? 'complete' : 'in progress'}: ${scope3Data.total_scope3_emissions.toFixed(1)} tCO₂e identified across value chain.`
              });
            }
          } else {
            newProgress.scope3 = {
              completion: 0,
              lastUpdated: 'Not started',
              extra: 'Value chain emissions (5 categories tracked)'
            };
          }
        } catch (e) {
          // Scope 3 module not yet migrated to PostgreSQL - silently default to "Not started"
          newProgress.scope3 = {
            completion: 0,
            lastUpdated: 'Not started',
            extra: 'Value chain emissions (5 categories tracked)'
          };
        }
      } else {
        newProgress.scope3 = {
          completion: 0,
          lastUpdated: 'Not started',
          extra: 'Value chain emissions (5 categories tracked)'
        };
      }
      
      // 6. Final Report Progress - Check report data availability
      try {
        const reportRes = await axios.get(`${API}/api/ers/report/${studyId}/data`, config);
        const reportData = reportRes.data;
        if (reportData) {
          // Calculate completion based on actual study data sections
          // Note: appendix.assumptions is excluded — it's always-present reference metadata, not study data
          let completedSections = 0;
          const totalSections = 7;
          if (reportData.baseline?.electricity?.total_kwh > 0) completedSections++;
          if (reportData.seu?.total_seus > 0) completedSections++;
          if (reportData.opportunities?.total_opportunities > 0) completedSections++;
          if (reportData.ghg?.total_tco2e > 0) completedSections++;
          if (reportData.macc?.macc_data?.length > 0) completedSections++;
          if (reportData.appendix?.time_series?.electricity?.length > 0) completedSections++;
          if (reportData.appendix?.onsite_measurements?.electrical?.length > 0) completedSections++;

          const reportCompletion = Math.round((completedSections / totalSections) * 100);
          
          newProgress.report = {
            completion: reportCompletion,
            lastUpdated: reportCompletion > 0 ? 'Data available' : 'Pending data',
            extra: `${completedSections}/${totalSections} data sections complete`
          };
        }
      } catch (e) {
        // Fallback calculation
        const reportProgress = Math.min(
          Math.round((newProgress.desktop.completion + newProgress.seu.completion + newProgress.onsite.completion + newProgress.opportunity.completion) / 4),
          100
        );
        
        newProgress.report = {
          completion: reportProgress > 20 ? reportProgress : 0,
          lastUpdated: reportProgress > 0 ? 'Ready to generate' : 'Pending prerequisites',
          extra: reportProgress >= 75 ? 'Ready for generation' : 'Complete other sections first'
        };
      }
      
      // Default insights if none generated
      if (insights.length === 0) {
        insights.push({
          type: 'info',
          text: 'Start by adding resource data in Desktop Study to begin your energy audit.'
        });
      }
      
      setProgressData(newProgress);
      setAiInsights(insights);
      
    } catch (error) {
      console.error('Error fetching study details:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'desktop-study', label: 'Desktop Study', icon: Database },
    { id: 'seu-identification', label: 'SEU Identification', icon: Zap },
    { id: 'onsite-study', label: 'On-Site Study', icon: MapPin },
    { id: 'scope3', label: 'Scope 3 Screening', icon: TrendingUp },
    // V1 + V2 hidden — V3 is the canonical Opportunity Engine. Components
    // and routes for V1/V2 are still mounted below so deep-links keep working.
    { id: 'opportunity-engine-v3', label: 'Opportunity Engine', icon: Target },
    { id: 'ghg-summary', label: 'GHG & Resource Summary', icon: BarChart3 },
    { id: 'trends', label: 'Trends & Charts', icon: TrendingUp },
    { id: 'final-report', label: 'Final Report', icon: FileCheck }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <div className="text-xl text-gray-600">Loading study...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      {/* Study-specific sub-header — sits below the global TabNavigation
          (h-16 → top-16). Lower z so the global nav's dropdowns sit above. */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/ers/studies')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-3 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Studies
          </button>

          {/* Project and Site Info */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900">{study?.study_name || 'ERS Study'}</h1>
            <p className="text-gray-600 text-sm mt-1">
              {site?.name || 'Site'} • {site?.location || 'Location'}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex overflow-x-auto">
            <div className="flex space-x-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (tab.externalPath) {
                        navigate(tab.externalPath(studyId));
                      } else {
                        setActiveTab(tab.id);
                      }
                    }}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'overview' && <OverviewTab study={study} site={site} progressData={progressData} aiInsights={aiInsights} />}
        {activeTab === 'desktop-study' && <DesktopStudyWrapper studyId={studyId} study={study} />}
        {activeTab === 'seu-identification' && <SEUModule studyIdProp={studyId} embedded />}
        {activeTab === 'onsite-study' && <OnSiteStudyTab study={study} site={site} />}
        {activeTab === 'scope3' && <Scope3Module study={study} />}
        {activeTab === 'opportunity-engine' && <OpportunityEngine study={study} />}
        {activeTab === 'opportunity-engine-v2' && <OpportunityEngineV2 study={study} />}
        {activeTab === 'opportunity-engine-v3' && <OpportunityEngineV3 study={study} />}
        {activeTab === 'ghg-summary' && <GHGSummaryTab study={study} site={site} />}
        {activeTab === 'trends' && <ERSTrends embedded />}
        {activeTab === 'final-report' && <FinalReport study={study} />}
      </div>
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ study, site, progressData, aiInsights }) {
  const [ghgData, setGhgData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const navigate = useNavigate();
  const { showInfo } = useAlert();

  const fetchOverviewData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Fetch GHG emissions
      const ghgRes = await axios.get(`${API}/api/ers/data/ghg-emissions/${study?.id}`, config);
      setGhgData(ghgRes.data);
      
      // Calculate metrics from GHG data
      setMetrics({
        totalEnergy: ghgRes.data.scope2.total_kwh || 0,
        totalFuel: ghgRes.data.scope1.fuel_breakdown.reduce((sum, f) => sum + (f.annual_qty || 0), 0),
        scope1: ghgRes.data.scope1.total_tco2e,
        scope2: ghgRes.data.scope2.total_tco2e,
        totalGhg: ghgRes.data.total_ghg.total_ghg_tco2e
      });
    } catch (error) {
      console.error('Error fetching overview data:', error);
    }
  };

  useEffect(() => {
    if (study?.id) {
      fetchOverviewData();
    }
  }, [study?.id]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content - 2 columns */}
      <div className="lg:col-span-2 space-y-6">
        
        {/* A. Study Progress Tracker */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Study Progress Tracker</h2>
          <div className="space-y-4">
            <ProgressStep 
              step="Desktop Analysis" 
              completion={progressData.desktop.completion} 
              lastUpdated={progressData.desktop.lastUpdated}
              extra={progressData.desktop.extra}
              onContinue={() => navigate(`/ers/study/${study?.id}?tab=desktop-study`)}
            />
            <ProgressStep 
              step="SEU Identification" 
              completion={progressData.seu.completion} 
              lastUpdated={progressData.seu.lastUpdated}
              extra={progressData.seu.extra}
              onContinue={() => navigate(`/ers/study/${study?.id}?tab=seu-identification`)}
            />
            <ProgressStep 
              step="On-Site Assessment" 
              completion={progressData.onsite.completion} 
              lastUpdated={progressData.onsite.lastUpdated}
              extra={progressData.onsite.extra}
              onContinue={() => navigate(`/ers/study/${study?.id}?tab=onsite-study`)}
            />
            <ProgressStep 
              step="Opportunity Engine" 
              completion={progressData.opportunity.completion} 
              lastUpdated={progressData.opportunity.lastUpdated}
              extra={progressData.opportunity.extra}
              onContinue={() => navigate(`/ers/study/${study?.id}?tab=opportunity-engine-v3`)}
            />
            <ProgressStep 
              step="Scope 3 Screening" 
              completion={progressData.scope3.completion}
              lastUpdated={progressData.scope3.lastUpdated}
              extra={progressData.scope3.extra || "Value chain emissions (6 categories)"}
              onContinue={() => navigate(`/ers/study/${study?.id}?tab=scope3`)}
            />
            <ProgressStep 
              step="ERS Final Report" 
              completion={progressData.report.completion} 
              lastUpdated={progressData.report.lastUpdated}
              extra={progressData.report.extra}
              onContinue={() => navigate(`/ers/study/${study?.id}?tab=final-report`)}
            />
          </div>
        </div>

        {/* B. Key Metrics Snapshot */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Key Metrics Snapshot</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Metric</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Value</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Source</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <MetricRow
                  metric="Total Energy (kWh/yr)"
                  value={metrics?.totalEnergy?.toLocaleString() || '0'}
                  source="Auto"
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=desktop-study`)}
                />
                <MetricRow
                  metric="Total Fuel (units/yr)"
                  value={metrics?.totalFuel?.toLocaleString() || '0'}
                  source="Auto"
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=desktop-study`)}
                />
                <MetricRow
                  metric="Scope 1 Emissions (tCO₂e)"
                  value={metrics?.scope1?.toLocaleString() || '0'}
                  source="Auto"
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=ghg-summary`)}
                />
                <MetricRow
                  metric="Scope 2 Emissions (tCO₂e)"
                  value={metrics?.scope2?.toLocaleString() || '0'}
                  source="Auto"
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=ghg-summary`)}
                />
                <MetricRow
                  metric="Energy Intensity (kWh/MT)"
                  value={metrics?.totalEnergy > 0 ? (metrics.totalEnergy / (study?.production_mt || 1)).toFixed(1) : '–'}
                  source="Auto"
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=desktop-study`)}
                />
                <MetricRow
                  metric="Total GHG (tCO₂e)"
                  value={metrics?.totalGhg > 0 ? metrics.totalGhg.toFixed(1) : '–'}
                  source="Auto"
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=ghg-summary`)}
                />
              </tbody>
            </table>
          </div>
        </div>

        {/* C. Data Completeness Indicators */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Data Completeness Indicators</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Data Category</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Details</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <DataCompletenessRow
                  category="Desktop Study"
                  status={progressData.desktop.completion >= 75 ? 'complete' : progressData.desktop.completion > 0 ? 'partial' : 'missing'}
                  missing={progressData.desktop.extra || 'Not started'}
                  action={progressData.desktop.completion > 0 ? 'View/Edit' : 'Start'}
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=desktop-study`)}
                />
                <DataCompletenessRow
                  category="SEU Identification"
                  status={progressData.seu.completion >= 75 ? 'complete' : progressData.seu.completion > 0 ? 'partial' : 'pending'}
                  missing={progressData.seu.extra || 'Not started'}
                  action={progressData.seu.completion > 0 ? 'View' : 'Start'}
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=seu-identification`)}
                />
                <DataCompletenessRow
                  category="On-Site Assessment"
                  status={progressData.onsite.completion >= 100 ? 'complete' : progressData.onsite.completion > 0 ? 'partial' : 'pending'}
                  missing={progressData.onsite.extra || 'Not started'}
                  action={progressData.onsite.completion > 0 ? 'View' : 'Start'}
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=onsite-study`)}
                />
                <DataCompletenessRow
                  category="Opportunity Engine"
                  status={progressData.opportunity.completion >= 100 ? 'complete' : progressData.opportunity.completion > 0 ? 'partial' : 'pending'}
                  missing={progressData.opportunity.extra || 'Not started'}
                  action={progressData.opportunity.completion > 0 ? 'View' : 'Run Analysis'}
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=opportunity-engine-v2`)}
                />
                <DataCompletenessRow
                  category="GHG & Resource Summary"
                  status={metrics?.totalGhg > 0 ? 'complete' : metrics?.totalEnergy > 0 || metrics?.scope1 > 0 ? 'partial' : 'pending'}
                  missing={metrics?.totalGhg > 0 ? `${metrics.totalGhg.toFixed(1)} tCO₂e total` : 'Needs SEU data'}
                  action="View"
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=ghg-summary`)}
                />
                <DataCompletenessRow
                  category="Final Report"
                  status={progressData.report.completion >= 75 ? 'complete' : progressData.report.completion > 0 ? 'partial' : 'pending'}
                  missing={progressData.report.extra || 'Not started'}
                  action={progressData.report.completion > 0 ? 'View' : 'Generate'}
                  onAction={() => navigate(`/ers/study/${study?.id}?tab=final-report`)}
                />
              </tbody>
            </table>
          </div>
        </div>

        {/* D. Quick Actions Panel */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <QuickActionButton 
              label="Start Desktop Study" 
              icon={Database}
              onClick={() => navigate(`/ers/study/${study?.id}?tab=desktop-study`)}
            />
            <QuickActionButton 
              label="Start On-Site Study" 
              icon={MapPin}
              onClick={() => navigate(`/ers/study/${study?.id}?tab=onsite-study`)}
            />
            <QuickActionButton 
              label="Add Equipment" 
              icon={Zap}
              onClick={() => navigate(`/ers/study/${study?.id}?tab=seu-identification`)}
            />
            <QuickActionButton
              label="Upload Data"
              icon={FileText}
              onClick={() => showInfo('CSV upload feature coming soon')}
            />
            <QuickActionButton
              label="Run AI Analysis"
              icon={Lightbulb}
              onClick={() => navigate(`/ers/study/${study?.id}?tab=opportunity-engine-v2`)}
            />
            <QuickActionButton 
              label="Scope 3 Screening" 
              icon={TrendingUp}
              onClick={() => navigate(`/ers/study/${study?.id}?tab=scope3`)}
            />
            <QuickActionButton 
              label="Generate Report" 
              icon={FileCheck}
              onClick={() => navigate(`/ers/study/${study?.id}?tab=final-report`)}
            />
          </div>
        </div>
      </div>

      {/* E. AI Insight Panel - Sidebar (1 column) */}
      <div className="lg:col-span-1">
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-md p-6 border-2 border-purple-200 sticky top-24">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-6 h-6 text-purple-600" />
            <h2 className="text-lg font-bold text-purple-900">AI Insights</h2>
          </div>
          <div className="space-y-4">
            {aiInsights.length > 0 ? (
              aiInsights.map((insight, idx) => (
                <div 
                  key={idx} 
                  className={`rounded-lg p-4 border ${
                    insight.type === 'action' ? 'bg-amber-50 border-amber-200' :
                    insight.type === 'opportunity' ? 'bg-green-50 border-green-200' :
                    insight.type === 'insight' ? 'bg-blue-50 border-blue-200' :
                    'bg-white border-purple-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {insight.type === 'action' && <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />}
                    {insight.type === 'opportunity' && <TrendingUp className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />}
                    {insight.type === 'insight' && <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />}
                    {insight.type === 'info' && <Info className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />}
                    <p className="text-sm text-gray-700">{insight.text}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-gray-500 text-center">
                  Add data to see AI-powered insights and recommendations.
                </p>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate(`/ers/study/${study?.id}?tab=opportunity-engine-v2`)}
            className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm"
          >
            View All Insights →
          </button>
        </div>
      </div>
    </div>
  );
}

// Progress Step Component
function ProgressStep({ step, completion, lastUpdated, extra, onContinue }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="font-semibold text-gray-900">{step}</h3>
          <p className="text-xs text-gray-500">
            {completion > 0 ? `Last updated: ${lastUpdated}` : lastUpdated}
          </p>
          {extra && (
            <p className="text-xs text-blue-600 font-medium mt-1">{extra}</p>
          )}
        </div>
        <span className={`text-lg font-bold ${completion === 100 ? 'text-green-600' : completion > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
          {completion}%
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div 
          className={`h-2 rounded-full transition-all ${completion === 100 ? 'bg-green-500' : 'bg-blue-600'}`}
          style={{ width: `${completion}%` }}
        ></div>
      </div>
      <button 
        onClick={onContinue}
        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
      >
        {completion === 0 ? 'Start →' : completion === 100 ? 'View →' : 'Continue →'}
      </button>
    </div>
  );
}

// Metric Row Component
function MetricRow({ metric, value, source, onAction }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-gray-900">{metric}</td>
      <td className="px-4 py-3 text-right font-semibold text-gray-900">{value}</td>
      <td className="px-4 py-3 text-gray-600">{source}</td>
      <td className="px-4 py-3 text-center">
        <button onClick={onAction} className="text-blue-600 hover:text-blue-800 text-xs font-medium">
          View Calculation
        </button>
      </td>
    </tr>
  );
}

// Data Completeness Row Component
function DataCompletenessRow({ category, status, missing, action, onAction }) {
  const statusColors = {
    complete: 'bg-green-100 text-green-800',
    partial: 'bg-amber-100 text-amber-800',
    missing: 'bg-red-100 text-red-800',
    pending: 'bg-gray-100 text-gray-800'
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-gray-900">{category}</td>
      <td className="px-4 py-3">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-600">{missing}</td>
      <td className="px-4 py-3">
        <button onClick={onAction} className="text-blue-600 hover:text-blue-800 text-xs font-medium">
          {action}
        </button>
      </td>
    </tr>
  );
}

// Quick Action Button Component
function QuickActionButton({ label, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-2 p-4 bg-gray-50 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-300 rounded-lg transition-all group"
    >
      <Icon className="w-6 h-6 text-gray-600 group-hover:text-blue-600" />
      <span className="text-xs font-medium text-gray-700 group-hover:text-blue-700 text-center">
        {label}
      </span>
    </button>
  );
}

function WorkflowStep({ number, title, description }) {
  return (
    <div className="flex items-start gap-3 bg-white rounded-lg p-3">
      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
        {number}
      </div>
      <div>
        <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
        <p className="text-gray-600 text-xs mt-1">{description}</p>
      </div>
    </div>
  );
}

// Desktop Study Wrapper (renders existing DesktopAssessment component)
function DesktopStudyWrapper({ studyId, study }) {
  return (
    <div className="bg-white rounded-xl shadow-md">
      <DesktopAssessment studyId={studyId} study={study} />
    </div>
  );
}


// Coming Soon Placeholder
function ComingSoonTab({ title }) {
  return (
    <div className="bg-white rounded-xl shadow-md p-12 text-center">
      <div className="max-w-md mx-auto">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lightbulb className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600">This feature is under development and will be available soon.</p>
      </div>
    </div>
  );
}

// ==================== ON-SITE STUDY TAB ====================
function OnSiteStudyTab({ study, site }) {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  // Tenant logo for the in-DOM cover (replaces hard-coded Ergoniq logo).
  const [tenantLogoDataUrl, setTenantLogoDataUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchTenantLogoDataUrl().then((url) => {
      if (!cancelled) setTenantLogoDataUrl(url);
    });
    return () => { cancelled = true; };
  }, []);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Report layout selector. 'sample' = new sample-style layout (matches the
  // VF_Desktop_Study_v2_Final.pdf reference doc and is the default).
  // 'classic' = the legacy table-only layout, kept as a fallback while the
  // per-industry generators that drive the sample layout's content are
  // built out. Persisted in localStorage so reloads keep the user's choice.
  const [reportFormat, setReportFormat] = useState(() => {
    try { return window.localStorage.getItem('onsite.reportFormat') || 'sample'; }
    catch { return 'sample'; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('onsite.reportFormat', reportFormat); } catch {}
  }, [reportFormat]);

  // Download PDF function
  const downloadPDF = async () => {
    if (!reportRef.current) return;

    setDownloadingPDF(true);
    try {
      await generateBrandedPdf({
        element: reportRef.current,
        filename: `OnSite_Report_${(study?.study_name || study?.name || 'Study').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        // The On-Site report DOM has its own cover (logo + title + site +
        // date), so suppress the helper's prepended cover. The last-page
        // platform attribution + AI disclaimer is provided by the helper
        // (single source of truth, no duplication risk). The helper also
        // stamps the per-page footer (company name + Page X of Y).
        skipCover: true,
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };
  
  // Print function
  const printReport = () => {
    if (!reportRef.current) return;
    const printContent = reportRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>On-Site Study Report - ${study?.name || 'Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f5f5f5; font-weight: 600; }
            h2 { color: #1e40af; margin-top: 20px; }
            h3 { color: #374151; margin-top: 15px; }
            .header { text-align: center; border-bottom: 2px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
            .logo { max-height: 50px; margin-bottom: 10px; }
            .footer { text-align: center; font-size: 10px; color: #666; border-top: 1px solid #ddd; padding-top: 15px; margin-top: 30px; }
            .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; }
            .badge-green { background: #d1fae5; color: #065f46; }
            .badge-yellow { background: #fef3c7; color: #92400e; }
            .badge-red { background: #fee2e2; color: #991b1b; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };
  
  useEffect(() => {
    const fetchSessions = async () => {
      if (!study?.id) return;
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(`${API}/api/ers/onsite/sessions/${study.id}`, config);
        setSessions(res.data || []);
      } catch (error) {
        console.error('Error fetching sessions:', error);
      } finally {
        setLoadingSessions(false);
      }
    };
    fetchSessions();
  }, [study?.id]);
  
  const activeSessions = sessions.filter(s => s.status !== 'Closed');
  const closedSessions = sessions.filter(s => s.status === 'Closed');
  
  // Generate consolidated report
  const generateReport = async () => {
    setReportLoading(true);
    setShowReport(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Fetch all session details with measurements
      const allMeasurements = {
        sessions: [],
        electrical: [],
        thermal: [],
        observations: [],
        insights: [],
        variance: []
      };
      
      for (const session of sessions) {
        // Get session details
        const sessionRes = await axios.get(`${API}/api/ers/onsite/session/${session.id}`, config);
        const sessionData = sessionRes.data;
        
        allMeasurements.sessions.push({
          session_id: session.session_id,
          date: session.measurement_date,
          shift: session.shift,
          condition: session.operating_condition,
          status: session.status,
          measured_by: session.measured_by
        });
        
        // Add electrical measurements
        if (sessionData.electrical_measurements) {
          sessionData.electrical_measurements.forEach(m => {
            const linkedSEU = sessionData.linked_seus?.find(s => s.seu_id === m.seu_id);
            allMeasurements.electrical.push({
              session_id: session.session_id,
              seu_code: linkedSEU?.seu_code || m.seu_id,
              equipment: linkedSEU?.desktop_data?.equipment_type || '-',
              voltage: m.avg_voltage_v,
              current: m.avg_current_a,
              power_kw: m.avg_power_kw,
              power_factor: m.power_factor,
              desktop_kwh: linkedSEU?.desktop_data?.final_annual_energy,
              measured_kwh: m.avg_power_kw ? Math.round(m.avg_power_kw * (linkedSEU?.desktop_data?.hours_per_day || 8) * 365) : null
            });
          });
        }
        
        // Add thermal measurements (from thermal collection)
        if (sessionData.thermal_measurements) {
          sessionData.thermal_measurements.forEach(m => {
            const linkedSEU = sessionData.linked_seus?.find(s => s.seu_id === m.seu_id);
            allMeasurements.thermal.push({
              session_id: session.session_id,
              seu_code: linkedSEU?.seu_code || m.seu_id,
              equipment: linkedSEU?.desktop_data?.equipment_type || '-',
              stack_temp: m.stack_temp_c,
              o2_percent: m.o2_percent,
              co_ppm: m.co_ppm,
              efficiency: m.estimated_efficiency_percent
            });
          });
        }
        
        // Add observations
        if (sessionData.observations) {
          sessionData.observations.forEach(obs => {
            allMeasurements.observations.push({
              session_id: session.session_id,
              seu_code: obs.seu_code || '-',
              type: obs.observation_type,
              description: obs.description,
              severity: obs.severity,
              impact: obs.efficiency_impact
            });
          });
        }
        
        // Get insights for this session
        try {
          const insightsRes = await axios.get(`${API}/api/ers/onsite/insights/${session.id}`, config);
          if (insightsRes.data?.insights) {
            insightsRes.data.insights.forEach(insight => {
              allMeasurements.insights.push({
                session_id: session.session_id,
                ...insight
              });
            });
          }
        } catch (e) {
          console.log('No insights for session', session.session_id);
        }
      }
      
      setReportData(allMeasurements);
    } catch (error) {
      console.error('Error generating report:', error);
      showError('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };
  
  // Generate AI narrative for all sessions
  const generateAINarrative = async () => {
    if (!reportData) {
      await generateReport();
    }
    
    setGeneratingAI(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Generate AI insights for the most recent active session or first completed session
      const targetSession = sessions.find(s => s.status !== 'Closed') || sessions[0];
      if (targetSession) {
        const aiRes = await axios.post(`${API}/api/ers/onsite/sessions/${targetSession.id}/generate-insights`, {}, config);
        setReportData(prev => ({
          ...prev,
          aiNarrative: aiRes.data.narrative,
          aiGeneratedAt: aiRes.data.generated_at
        }));
      }
    } catch (error) {
      console.error('Error generating AI narrative:', error);
      showError('Failed to generate AI narrative');
    } finally {
      setGeneratingAI(false);
    }
  };
  
  // ---------- AI narrative renderer ----------
  // The backend's AI service can return either:
  //   (a) a structured JSON document with sections / priority_actions /
  //       estimated_savings, or
  //   (b) plain markdown-ish prose.
  // The previous formatter assumed (b) only, so JSON output rendered as
  // escaped text noise. Try JSON first; fall back to the markdown path.

  const renderInline = (text) => {
    // Convert **bold** to <strong>; everything else as plain text. Returns
    // an array of React children.
    const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );
  };

  // Try to interpret the narrative payload as JSON. The backend has been
  // observed returning either a top-level object or a string that itself
  // contains JSON; handle both. Returns the parsed object on success or
  // null if it doesn't look like JSON.
  const tryParseJsonNarrative = (text) => {
    if (text == null) return null;
    if (typeof text === 'object') return text;
    const s = String(text).trim();
    if (!s) return null;
    if (!(s.startsWith('{') || s.startsWith('['))) return null;
    try { return JSON.parse(s); } catch { return null; }
  };

  // Render a single "section" object: { title, content, findings,
  // recommendations, subsections }. Used both at the top level and
  // recursively for nested subsections.
  const renderSection = (section, key, depth = 0) => {
    if (!section || typeof section !== 'object') return null;
    const Heading = depth === 0 ? 'h3' : 'h4';
    const headingClass = depth === 0
      ? 'text-base font-bold text-indigo-800 mt-5 mb-2'
      : 'text-sm font-semibold text-gray-800 mt-3 mb-1';
    return (
      <div key={key} className="mb-3">
        {section.title && <Heading className={headingClass}>{section.title}</Heading>}
        {section.content && (
          <p className="text-gray-700 mb-2 leading-relaxed text-justify">
            {renderInline(section.content)}
          </p>
        )}
        {Array.isArray(section.findings) && section.findings.length > 0 && (
          <>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Findings</p>
            <ul className="list-disc pl-6 mb-2 space-y-1 text-gray-700">
              {section.findings.map((f, i) => (
                <li key={i} className="leading-relaxed">{typeof f === 'string' ? renderInline(f) : JSON.stringify(f)}</li>
              ))}
            </ul>
          </>
        )}
        {Array.isArray(section.recommendations) && section.recommendations.length > 0 && (
          <>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Recommendations</p>
            <ul className="list-disc pl-6 mb-2 space-y-1 text-gray-700">
              {section.recommendations.map((r, i) => (
                <li key={i} className="leading-relaxed">{typeof r === 'string' ? renderInline(r) : JSON.stringify(r)}</li>
              ))}
            </ul>
          </>
        )}
        {Array.isArray(section.subsections) && section.subsections.map((sub, i) =>
          renderSection(sub, `${key}-sub-${i}`, depth + 1)
        )}
      </div>
    );
  };

  // Render priority_actions: { immediate: [{action, impact, investment?}], short_term: [...], medium_term: [...] }
  const renderPriorityActions = (pa) => {
    if (!pa || typeof pa !== 'object') return null;
    const tiers = [
      ['immediate', 'Immediate (0–3 months)'],
      ['short_term', 'Short-term (3–12 months)'],
      ['medium_term', 'Medium-term (1–3 years)'],
      ['long_term', 'Long-term (3+ years)'],
    ];
    const visible = tiers.filter(([k]) => Array.isArray(pa[k]) && pa[k].length > 0);
    if (visible.length === 0) return null;
    return (
      <div className="mb-4">
        <h3 className="text-base font-bold text-indigo-800 mt-5 mb-2">Priority Actions</h3>
        {visible.map(([k, label]) => (
          <div key={k} className="mb-3">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</p>
            <table className="w-full text-sm border border-gray-200 mb-2">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left border-b border-gray-200 font-semibold text-gray-700">Action</th>
                  <th className="px-3 py-2 text-left border-b border-gray-200 font-semibold text-gray-700">Impact</th>
                  {pa[k].some(x => x?.investment) && (
                    <th className="px-3 py-2 text-right border-b border-gray-200 font-semibold text-gray-700">Investment</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pa[k].map((item, i) => (
                  <tr key={i} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 align-top">{item?.action || '—'}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{item?.impact || '—'}</td>
                    {pa[k].some(x => x?.investment) && (
                      <td className="px-3 py-2 align-top text-right text-gray-700">{item?.investment || ''}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  // Render estimated_savings: { summary, items: [{area, savings, unit}] }
  const renderEstimatedSavings = (es) => {
    if (!es || typeof es !== 'object') return null;
    const hasItems = Array.isArray(es.items) && es.items.length > 0;
    if (!es.summary && !hasItems) return null;
    return (
      <div className="mb-4">
        <h3 className="text-base font-bold text-indigo-800 mt-5 mb-2">Estimated Savings</h3>
        {es.summary && (
          <p className="text-gray-700 mb-2 leading-relaxed">{renderInline(String(es.summary))}</p>
        )}
        {hasItems && (
          <table className="w-full text-sm border border-gray-200 mb-2">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left border-b border-gray-200 font-semibold text-gray-700">Area</th>
                <th className="px-3 py-2 text-right border-b border-gray-200 font-semibold text-gray-700">Savings</th>
                <th className="px-3 py-2 text-left border-b border-gray-200 font-semibold text-gray-700">Unit</th>
              </tr>
            </thead>
            <tbody>
              {es.items.map((it, i) => (
                <tr key={i} className="border-b border-gray-100 last:border-0">
                  <td className="px-3 py-2">{it?.area || '—'}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {typeof it?.savings === 'number' ? it.savings.toLocaleString('en-IN') : (it?.savings ?? '')}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{it?.unit || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderJsonNarrative = (data) => {
    if (!data) return null;
    const out = [];

    // Top-level "summary" or "overview" prose, if present
    if (typeof data.summary === 'string' && data.summary.trim()) {
      out.push(
        <p key="summary" className="text-gray-700 mb-3 leading-relaxed text-justify">
          {renderInline(data.summary)}
        </p>
      );
    }

    // Sections list — accept several common shapes the LLM may emit
    const sectionLists = [
      data.sections, data.analysis, data.detailed_analysis,
      Array.isArray(data) ? data : null,
    ].filter(Boolean);
    sectionLists.forEach((list, i) => {
      if (Array.isArray(list)) {
        list.forEach((s, j) => {
          const node = renderSection(s, `sec-${i}-${j}`);
          if (node) out.push(node);
        });
      }
    });

    if (data.priority_actions) {
      const node = renderPriorityActions(data.priority_actions);
      if (node) out.push(<React.Fragment key="pa">{node}</React.Fragment>);
    }
    if (data.estimated_savings) {
      const node = renderEstimatedSavings(data.estimated_savings);
      if (node) out.push(<React.Fragment key="es">{node}</React.Fragment>);
    }

    // If nothing structured matched, last-resort: walk top-level
    // string/array fields so nothing is lost (avoids a blank section).
    if (out.length === 0 && typeof data === 'object') {
      Object.entries(data).forEach(([k, v], i) => {
        if (v == null) return;
        if (typeof v === 'string') {
          out.push(
            <div key={`fld-${i}`} className="mb-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{k.replace(/_/g, ' ')}</p>
              <p className="text-gray-700 leading-relaxed">{renderInline(v)}</p>
            </div>
          );
        }
      });
    }

    return out.length > 0 ? out : null;
  };

  const formatNarrative = (text) => {
    if (!text) return null;
    const parsed = tryParseJsonNarrative(text);
    if (parsed) {
      const node = renderJsonNarrative(parsed);
      if (node) return node;
    }
    const lines = String(text).split('\n');
    const out = [];
    let bulletBuffer = [];

    const flushBullets = () => {
      if (bulletBuffer.length === 0) return;
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 mb-3 space-y-1 text-gray-700">
          {bulletBuffer.map((b, i) => (
            <li key={i} className="leading-relaxed">{renderInline(b)}</li>
          ))}
        </ul>
      );
      bulletBuffer = [];
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        flushBullets();
        return; // drop empty lines — vertical rhythm comes from block margins
      }
      // Heading-only "**Bold heading**" line
      if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length > 4) {
        flushBullets();
        out.push(
          <h3 key={idx} className="text-base font-bold text-gray-900 mt-4 mb-2">
            {trimmed.replace(/\*\*/g, '')}
          </h3>
        );
        return;
      }
      // Markdown headings
      if (trimmed.startsWith('#')) {
        flushBullets();
        const level = (trimmed.match(/^#+/) || [''])[0].length;
        const headingText = trimmed.replace(/^#+\s*/, '').replace(/\*\*/g, '');
        if (level <= 2) {
          out.push(<h2 key={idx} className="text-lg font-bold text-indigo-700 mt-5 mb-2">{headingText}</h2>);
        } else {
          out.push(<h4 key={idx} className="text-sm font-semibold text-gray-800 mt-3 mb-1">{headingText}</h4>);
        }
        return;
      }
      // Bullets — accumulate
      if (trimmed.startsWith('-') || trimmed.startsWith('•') || trimmed.startsWith('*')) {
        bulletBuffer.push(trimmed.replace(/^[-•*]\s*/, ''));
        return;
      }
      // Numbered: render as ordered-style line with hanging indent
      if (/^\d+\./.test(trimmed)) {
        flushBullets();
        out.push(
          <p key={idx} className="text-gray-700 mb-1 pl-6 -indent-6 leading-relaxed">
            {renderInline(trimmed)}
          </p>
        );
        return;
      }
      // Plain paragraph
      flushBullets();
      out.push(
        <p key={idx} className="text-gray-700 mb-2 leading-relaxed text-justify">
          {renderInline(trimmed)}
        </p>
      );
    });
    flushBullets();
    return out;
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">On-Site Measurement Module</h2>
            <p className="text-blue-100 max-w-2xl">
              Record field measurements for your SEUs. Create measurement sessions, capture electrical/thermal data, 
              document observations, and verify desktop estimates against actual on-site readings.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={generateReport}
              disabled={sessions.length === 0 || reportLoading}
              className="px-4 py-2.5 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={sessions.length === 0 ? 'No sessions to report' : 'View consolidated report'}
            >
              <FileText className="w-5 h-5" />
              On-Site Report
            </button>
            <button
              onClick={() => navigate(`/ers/study/${study?.id}/onsite`)}
              className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2 shadow-lg"
            >
              <MapPin className="w-5 h-5" />
              Open Measurement Module
            </button>
          </div>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
              <p className="text-xs text-gray-500">Total Sessions</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{closedSessions.length}</p>
              <p className="text-xs text-gray-500">Completed</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{activeSessions.length}</p>
              <p className="text-xs text-gray-500">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Camera className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">-</p>
              <p className="text-xs text-gray-500">Evidence Files</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sessions List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Measurement Sessions</h3>
        </div>
        
        {loadingSessions ? (
          <div className="p-8 text-center text-gray-500">
            Loading sessions...
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-8 text-center">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h4 className="text-lg font-medium text-gray-700 mb-2">No Measurement Sessions Yet</h4>
            <p className="text-gray-500 mb-4">
              Start your first on-site measurement session to record field data for your SEUs.
            </p>
            <button
              onClick={() => navigate(`/ers/study/${study?.id}/onsite`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Start First Session
            </button>
          </div>
        ) : (
          <div className="divide-y">
            {sessions.map(session => (
              <div
                key={session.id}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                onClick={() => navigate(`/ers/study/${study?.id}/onsite`)}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${
                    session.status === 'Closed' ? 'bg-gray-100' :
                    session.safety_completed ? 'bg-green-100' : 'bg-yellow-100'
                  }`}>
                    {session.status === 'Closed' ? (
                      <CheckCircle className="w-5 h-5 text-gray-500" />
                    ) : session.safety_completed ? (
                      <Activity className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{session.session_id}</p>
                    <p className="text-sm text-gray-600">
                      {session.measurement_date} • {session.shift} Shift • {session.operating_condition}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    session.status === 'Closed' ? 'bg-gray-100 text-gray-700' :
                    session.safety_completed ? 'bg-green-100 text-green-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {session.status === 'Closed' ? 'Completed' :
                     session.safety_completed ? 'In Progress' : 'Safety Pending'}
                  </span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Info Card */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <Sparkles className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="font-bold text-purple-900 mb-1">On-Site Workflow</h3>
            <p className="text-sm text-purple-700 mb-3">
              The measurement module follows a structured workflow to ensure complete and auditable data capture:
            </p>
            <ol className="text-sm text-purple-700 space-y-1 list-decimal list-inside">
              <li>Complete Safety & Permit Checklist (mandatory)</li>
              <li>Select SEUs from your desktop inventory</li>
              <li>Record electrical/thermal measurements</li>
              <li>Document observations and evidence</li>
              <li>Complete verification checklist</li>
              <li>Review summary and close session</li>
            </ol>
          </div>
        </div>
      </div>
      
      {/* Report Modal */}
      {showReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">On-Site Study Report</h2>
                <p className="text-blue-100 text-sm">{study?.name} • {sessions.length} Sessions</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Layout selector — sample style (new default) vs the
                    classic table layout (kept as fallback while the
                    per-industry content generators are built out). */}
                <div className="flex items-center bg-white/10 rounded-lg p-0.5 text-xs">
                  <button
                    onClick={() => setReportFormat('sample')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                      reportFormat === 'sample'
                        ? 'bg-white text-blue-700'
                        : 'text-white/80 hover:text-white'
                    }`}
                    title="Sample-style layout (matches the reference VF Desktop Study format)"
                  >
                    Sample
                  </button>
                  <button
                    onClick={() => setReportFormat('classic')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                      reportFormat === 'classic'
                        ? 'bg-white text-blue-700'
                        : 'text-white/80 hover:text-white'
                    }`}
                    title="Legacy classic layout (fallback)"
                  >
                    Classic
                  </button>
                </div>
                <button
                  onClick={generateAINarrative}
                  disabled={generatingAI || reportLoading}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {generatingAI ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate AI Insights
                    </>
                  )}
                </button>
                <button
                  onClick={printReport}
                  disabled={reportLoading}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                  title="Print Report"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={downloadPDF}
                  disabled={downloadingPDF || reportLoading}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                  title="Download PDF"
                >
                  {downloadingPDF ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      PDF
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowReport(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {reportLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                  <span className="ml-3 text-gray-600">Generating report...</span>
                </div>
              ) : reportData ? (
                <div ref={reportRef}>
                {reportFormat === 'sample' ? (
                  <OnSiteStudySampleReport
                    study={study}
                    site={site}
                    reportData={reportData}
                    tenantLogoDataUrl={tenantLogoDataUrl}
                    tenantName={getCurrentTenantName()}
                  />
                ) : (
                <>
                  {/* PDF/Print Header — tenant logo (or "Your logo here" placeholder) */}
                  <div className="text-center border-b-2 border-blue-600 pb-4 mb-6">
                    {tenantLogoDataUrl ? (
                      <img src={tenantLogoDataUrl} alt="Company logo" className="max-h-16 mx-auto mb-3 object-contain" />
                    ) : (
                      <div
                        className="mx-auto mb-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
                        style={{ width: '220px', height: '64px', paddingTop: '22px', textAlign: 'center', boxSizing: 'border-box' }}
                      >
                        <span className="text-sm italic text-gray-400">Your logo here</span>
                      </div>
                    )}
                    <h1 className="text-2xl font-bold text-gray-900">On-Site Study Report</h1>
                    {getCurrentTenantName() && (
                      <p className="text-base font-semibold text-gray-800 mt-1">{getCurrentTenantName()}</p>
                    )}
                    <p className="text-gray-600">{study?.name} • {site?.name || 'Site'}</p>
                    <p className="text-sm text-gray-500">Generated: {formatReportDate(new Date())}</p>
                  </div>
                  
                  {/* Sessions Summary Table */}
                  <div className="bg-white rounded-lg border overflow-hidden mb-6">
                    <div className="px-4 py-3 bg-gray-50 border-b">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <ClipboardCheck className="w-5 h-5 text-blue-600" />
                        Measurement Sessions ({reportData.sessions?.length || 0})
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-gray-600 font-medium">Session</th>
                            <th className="px-4 py-2 text-left text-gray-600 font-medium">Date</th>
                            <th className="px-4 py-2 text-left text-gray-600 font-medium">Shift</th>
                            <th className="px-4 py-2 text-left text-gray-600 font-medium">Condition</th>
                            <th className="px-4 py-2 text-left text-gray-600 font-medium">Measured By</th>
                            <th className="px-4 py-2 text-left text-gray-600 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {reportData.sessions?.map((s, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium text-blue-600">{s.session_id}</td>
                              <td className="px-4 py-2 text-gray-700">{s.date}</td>
                              <td className="px-4 py-2 text-gray-700">{s.shift}</td>
                              <td className="px-4 py-2 text-gray-700">{s.condition}</td>
                              <td className="px-4 py-2 text-gray-700">{s.measured_by}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  s.status === 'Closed' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'
                                }`}>{s.status === 'Closed' ? 'Completed' : 'In Progress'}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Electrical Measurements Table */}
                  {reportData.electrical?.length > 0 && (
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="px-4 py-3 bg-yellow-50 border-b">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Zap className="w-5 h-5 text-yellow-600" />
                          Electrical Measurements ({reportData.electrical.length})
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Session</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">SEU Code</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Equipment</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">Voltage (V)</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">Current (A)</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">Power (kW)</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">PF</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">Desktop kWh/yr</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">Measured kWh/yr</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">Variance</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {reportData.electrical.map((m, idx) => {
                              const variance = m.desktop_kwh && m.measured_kwh ? 
                                ((m.measured_kwh - m.desktop_kwh) / m.desktop_kwh * 100).toFixed(1) : null;
                              return (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 text-gray-600">{m.session_id}</td>
                                  <td className="px-4 py-2 font-medium text-yellow-700">{m.seu_code}</td>
                                  <td className="px-4 py-2 text-gray-700">{m.equipment}</td>
                                  <td className="px-4 py-2 text-right text-gray-700">{m.voltage?.toFixed(1) || '-'}</td>
                                  <td className="px-4 py-2 text-right text-gray-700">{m.current?.toFixed(1) || '-'}</td>
                                  <td className="px-4 py-2 text-right text-gray-700">{m.power_kw?.toFixed(1) || '-'}</td>
                                  <td className="px-4 py-2 text-right text-gray-700">{m.power_factor?.toFixed(2) || '-'}</td>
                                  <td className="px-4 py-2 text-right text-gray-600">{m.desktop_kwh?.toLocaleString() || '-'}</td>
                                  <td className="px-4 py-2 text-right text-gray-600">{m.measured_kwh?.toLocaleString() || '-'}</td>
                                  <td className={`px-4 py-2 text-right font-medium ${
                                    variance ? (parseFloat(variance) < 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-400'
                                  }`}>
                                    {variance ? `${variance}%` : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Thermal Measurements Table */}
                  {reportData.thermal?.length > 0 && (
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="px-4 py-3 bg-orange-50 border-b">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Flame className="w-5 h-5 text-orange-600" />
                          Thermal/Combustion Measurements ({reportData.thermal.length})
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Session</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">SEU Code</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Equipment</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">Stack Temp (°C)</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">O₂ (%)</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">CO (ppm)</th>
                              <th className="px-4 py-2 text-right text-gray-600 font-medium">Efficiency (%)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {reportData.thermal.map((m, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-gray-600">{m.session_id}</td>
                                <td className="px-4 py-2 font-medium text-orange-700">{m.seu_code}</td>
                                <td className="px-4 py-2 text-gray-700">{m.equipment}</td>
                                <td className="px-4 py-2 text-right text-gray-700">{m.stack_temp || '-'}</td>
                                <td className="px-4 py-2 text-right text-gray-700">{m.o2_percent || '-'}</td>
                                <td className="px-4 py-2 text-right text-gray-700">{m.co_ppm || '-'}</td>
                                <td className="px-4 py-2 text-right text-gray-700">{m.efficiency || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Observations Table */}
                  {reportData.observations?.length > 0 && (
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="px-4 py-3 bg-purple-50 border-b">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Camera className="w-5 h-5 text-purple-600" />
                          Field Observations ({reportData.observations.length})
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Session</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">SEU</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Type</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Description</th>
                              <th className="px-4 py-2 text-center text-gray-600 font-medium">Severity</th>
                              <th className="px-4 py-2 text-center text-gray-600 font-medium">Impact</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {reportData.observations.map((obs, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-gray-600">{obs.session_id}</td>
                                <td className="px-4 py-2 font-medium text-purple-700">{obs.seu_code}</td>
                                <td className="px-4 py-2 text-gray-700">{obs.type}</td>
                                <td className="px-4 py-2 text-gray-700 max-w-xs truncate">{obs.description}</td>
                                <td className="px-4 py-2 text-center">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    obs.severity === 'High' ? 'bg-red-100 text-red-700' :
                                    obs.severity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>{obs.severity}</span>
                                </td>
                                <td className="px-4 py-2 text-center">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    obs.impact === 'High' ? 'bg-red-100 text-red-700' :
                                    obs.impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>{obs.impact}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Rule-Based Insights Table */}
                  {reportData.insights?.length > 0 && (
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="px-4 py-3 bg-blue-50 border-b">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Lightbulb className="w-5 h-5 text-blue-600" />
                          Auto-Detected Insights ({reportData.insights.length})
                        </h3>
                      </div>
                      <div className="p-4 space-y-2">
                        {reportData.insights.map((insight, idx) => (
                          <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                            insight.type === 'critical' ? 'bg-red-50 border-red-500' :
                            insight.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                            'bg-green-50 border-green-500'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  insight.type === 'critical' ? 'bg-red-100 text-red-700' :
                                  insight.type === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>{insight.category}</span>
                                <span className="text-xs text-gray-500 ml-2">{insight.seu_code}</span>
                              </div>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mt-1">{insight.trigger}</p>
                            <p className="text-xs text-gray-600">{insight.recommendation}</p>
                            <p className="text-xs text-gray-500 mt-1">Potential: {insight.potential_savings}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* AI Narrative Section */}
                  {reportData.aiNarrative && (
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 overflow-hidden">
                      <div className="px-4 py-3 bg-indigo-100 border-b border-indigo-200">
                        <h3 className="font-semibold text-indigo-900 flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-indigo-600" />
                          AI-Generated Analysis
                        </h3>
                        {reportData.aiGeneratedAt && (
                          <p className="text-xs text-indigo-600 mt-1">
                            Generated: {formatDateTime(reportData.aiGeneratedAt)}
                          </p>
                        )}
                      </div>
                      <div className="p-6 prose prose-sm max-w-none">
                        {formatNarrative(reportData.aiNarrative)}
                      </div>
                    </div>
                  )}
                  
                  {/* Empty state for no AI narrative */}
                  {!reportData.aiNarrative && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 text-center print:hidden">
                      <Sparkles className="w-10 h-10 text-indigo-400 mx-auto mb-2" />
                      <h4 className="font-medium text-gray-700 mb-1">AI Narrative Not Generated</h4>
                      <p className="text-sm text-gray-500 mb-3">Click "Generate AI Insights" to create an AI-powered analysis of your measurement data.</p>
                    </div>
                  )}
                  
                  {/* The platform-attribution + AI-disclaimer footer is
                      provided by the shared PDF helper (generateBrandedPdf)
                      so there is only ever one source of truth and we
                      cannot accidentally duplicate it. */}
                </>
                )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No data available
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Report generated from {sessions.length} measurement session{sessions.length !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowReport(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== OPPORTUNITY ENGINE TAB ====================
function OpportunityEngineTab({ study }) {
  const [expandedSections, setExpandedSections] = useState({
    identified: true,
    financial: false,
    ai: false
  });

  // Placeholder opportunities
  const [opportunities] = useState([
    { 
      id: 1, 
      title: 'Compressed Air Leak Repair', 
      category: 'Quick Win',
      source: 'Electrical SEU',
      annual_savings_kwh: 45000,
      annual_savings_cost: 315000,
      investment: 50000,
      payback_months: 2,
      status: 'identified',
      confidence: 'high'
    },
    { 
      id: 2, 
      title: 'VFD on Cooling Tower Fans', 
      category: 'Medium Term',
      source: 'Electrical SEU',
      annual_savings_kwh: 125000,
      annual_savings_cost: 875000,
      investment: 800000,
      payback_months: 11,
      status: 'evaluated',
      confidence: 'medium'
    },
    { 
      id: 3, 
      title: 'Heat Recovery from Furnace Flue', 
      category: 'Strategic',
      source: 'Fuel SEU',
      annual_savings_kwh: 0,
      annual_savings_gj: 850,
      annual_savings_cost: 1200000,
      investment: 3500000,
      payback_months: 35,
      status: 'identified',
      confidence: 'medium'
    },
  ]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const totalSavings = opportunities.reduce((sum, o) => sum + (o.annual_savings_cost || 0), 0);
  const totalInvestment = opportunities.reduce((sum, o) => sum + (o.investment || 0), 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Target className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{opportunities.length}</p>
              <p className="text-xs text-green-600">Opportunities Identified</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">₹{(totalSavings / 100000).toFixed(1)}L</p>
              <p className="text-xs text-blue-600">Annual Savings Potential</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">₹{(totalInvestment / 100000).toFixed(1)}L</p>
              <p className="text-xs text-amber-600">Total Investment Required</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Leaf className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">285</p>
              <p className="text-xs text-purple-600">tCO₂e Reduction Potential</p>
            </div>
          </div>
        </div>
      </div>

      {/* Identified Opportunities */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => toggleSection('identified')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-bold text-gray-900">Identified Opportunities</h2>
          </div>
          {expandedSections.identified ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
        </button>

        {expandedSections.identified && (
          <div className="p-6">
            {/* Filter Row */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">All</button>
              <button className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-200">Quick Wins ({"<"}6 mo)</button>
              <button className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-200">Medium Term (6-24 mo)</button>
              <button className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-200">Strategic ({">"}24 mo)</button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Opportunity</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">
                      Annual Savings
                      <span className="ml-1 cursor-help" title="Calculated from SEU data × efficiency improvement">
                        <Info className="w-3 h-3 inline text-gray-400" />
                      </span>
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Investment</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">
                      Payback
                      <span className="ml-1 cursor-help" title="Investment ÷ Annual Savings × 12">
                        <Info className="w-3 h-3 inline text-gray-400" />
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Confidence</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {opportunities.map((opp, idx) => (
                    <tr key={opp.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{opp.title}</div>
                        <div className="text-xs text-gray-500">Source: {opp.source}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          opp.category === 'Quick Win' ? 'bg-green-100 text-green-700' :
                          opp.category === 'Medium Term' ? 'bg-blue-100 text-blue-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {opp.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">₹{(opp.annual_savings_cost / 1000).toFixed(0)}K</td>
                      <td className="px-4 py-3 text-right text-gray-900">₹{(opp.investment / 1000).toFixed(0)}K</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${opp.payback_months < 12 ? 'text-green-600' : opp.payback_months < 24 ? 'text-amber-600' : 'text-gray-600'}`}>
                          {opp.payback_months} mo
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          opp.confidence === 'high' ? 'bg-green-100 text-green-700' :
                          opp.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {opp.confidence}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          opp.status === 'evaluated' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {opp.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
                          Evaluate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-md">
                <Plus className="w-4 h-4" />
                Add Opportunity
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">
                <Download className="w-4 h-4" />
                Export to Excel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Financial Calculator */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => toggleSection('financial')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Calculator className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-bold text-gray-900">Financial Calculator</h2>
          </div>
          {expandedSections.financial ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
        </button>

        {expandedSections.financial && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4">Input Parameters</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Investment (₹)</label>
                    <input type="number" placeholder="500000" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Annual Savings (₹)</label>
                    <input type="number" placeholder="180000" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Equipment Life (years)</label>
                    <input type="number" placeholder="10" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Discount Rate (%)</label>
                    <input type="number" placeholder="12" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h4 className="font-semibold text-gray-900 mb-4">Calculated Results</h4>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Simple Payback</span>
                    <span className="font-bold text-blue-600">33.3 months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">NPV (Net Present Value)</span>
                    <span className="font-bold text-green-600">₹5,17,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">IRR (Internal Rate of Return)</span>
                    <span className="font-bold text-green-600">34.2%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ROI (Return on Investment)</span>
                    <span className="font-bold text-green-600">260%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AI Recommendations */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-md overflow-hidden border border-purple-200">
        <button
          onClick={() => toggleSection('ai')}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-purple-100/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-purple-900">AI-Powered Recommendations</h2>
            <span className="px-2 py-1 bg-purple-200 text-purple-700 rounded-full text-xs font-medium">Beta</span>
          </div>
          {expandedSections.ai ? <ChevronDown className="w-5 h-5 text-purple-500" /> : <ChevronRight className="w-5 h-5 text-purple-500" />}
        </button>

        {expandedSections.ai && (
          <div className="p-6 space-y-4">
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Lightbulb className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">High Priority: Compressed Air System</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Based on your SEU data, the compressed air system consumes 24% of electrical energy. 
                    Leak detection and repair typically yields 15-30% savings with payback under 6 months.
                  </p>
                  <div className="mt-2 text-xs text-purple-600">
                    <strong>Data source:</strong> Electrical SEU Table, Row 1
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Consider: Solar PV Installation</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Your Scope 2 emissions (1,080 tCO₂e) are 85% of total GHG. 
                    A rooftop solar installation could reduce this by 40-60% with current subsidies available.
                  </p>
                  <div className="mt-2 text-xs text-purple-600">
                    <strong>Data source:</strong> GHG Summary, Scope 2 calculation
                  </div>
                </div>
              </div>
            </div>
            <button className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" />
              Generate More Recommendations
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== GHG & RESOURCE SUMMARY TAB ====================
function GHGSummaryTab({ study, site }) {
  const { showError } = useAlert();
  const [ghgData, setGhgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [generatingAIReport, setGeneratingAIReport] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchGHGData();
  }, [study]);

  const fetchGHGData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [ghgRes, scope3Res] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ers/data/ghg-emissions/${study?.id}`, config),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/scope3/hotspot/${study?.id}`, config).catch(() => ({ data: { total_scope3_emissions: 0, categories: [] } }))
      ]);
      
      const ghgDataWithScope3 = {
        ...ghgRes.data,
        scope3: {
          total_tco2e: scope3Res.data.total_scope3_emissions || 0,
          categories: scope3Res.data.categories || []
        }
      };
      
      setGhgData(ghgDataWithScope3);
    } catch (error) {
      console.error('Error fetching GHG data:', error);
      // Set minimal data structure
      setGhgData({
        scope1: { total_tco2e: 0, fuel_breakdown: [] },
        scope2: { total_tco2e: 0, total_kwh: 0, grid_factor: 0.716, grid_region: "India" },
        scope3: { total_tco2e: 0, categories: [] },
        total_ghg: { scope1_tco2e: 0, scope2_tco2e: 0, total_ghg_tco2e: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!ghgData) return;
    
    // Create CSV content
    const csvContent = [
      ['GHG Emissions Summary Report'],
      ['Study:', study?.study_name || 'Energy Audit'],
      ['Generated:', formatDate(new Date())],
      [],
      ['SCOPE 1 EMISSIONS (Direct)'],
      ['Source', 'Emissions (tCO2e)'],
      ...(ghgData.scope1?.fuel_breakdown
        ? (Array.isArray(ghgData.scope1.fuel_breakdown)
            ? ghgData.scope1.fuel_breakdown.map(f => [f.fuel_type, f.emissions_tco2e?.toFixed(1) || '0'])
            : Object.entries(ghgData.scope1.fuel_breakdown).map(([k, v]) => [k, Number(v).toFixed(1)]))
        : []),
      ['Total Scope 1', ghgData.scope1?.total_tco2e?.toFixed(1) || '0'],
      [],
      ['SCOPE 2 EMISSIONS (Indirect)'],
      ['Metric', 'Value'],
      ['Total Electricity (kWh)', ghgData.scope2?.total_kwh?.toLocaleString() || '0'],
      ['Grid Region', study?.currency === 'SGD' ? 'Singapore' : 'India'],
      ['Grid Emission Factor (kgCO2/kWh)', ghgData.scope2?.grid_factor || '0.716'],
      ['Total Scope 2 (tCO2e)', ghgData.scope2?.total_tco2e?.toFixed(1) || '0'],
      [],
      ['TOTAL GHG EMISSIONS'],
      ['Scope 1 (tCO2e)', ghgData.total_ghg?.scope1_tco2e?.toFixed(1) || '0'],
      ['Scope 2 (tCO2e)', ghgData.total_ghg?.scope2_tco2e?.toFixed(1) || '0'],
      ['Total (tCO2e)', ghgData.total_ghg?.total_ghg_tco2e?.toFixed(1) || '0'],
      [],
      ['RESOURCE CONSUMPTION'],
      ['Resource', 'Consumption', 'Unit', 'Emissions (tCO2e)'],
      ['Electricity', ghgData.scope2?.total_kwh || '0', 'kWh', ghgData.scope2?.total_tco2e?.toFixed(1) || '0'],
      ...(ghgData.scope1?.fuel_breakdown
        ? (Array.isArray(ghgData.scope1.fuel_breakdown)
            ? ghgData.scope1.fuel_breakdown.map(f => [f.fuel_type, f.annual_qty || '0', f.unit || '', f.emissions_tco2e?.toFixed(1) || '0'])
            : Object.entries(ghgData.scope1.fuel_breakdown).map(([k, v]) => [k, '', '', Number(v).toFixed(1)]))
        : [])
    ];
    
    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `GHG_Summary_${study?.study_name?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Download PDF Report — uses the shared branded-PDF helper so the output has
  // a cover page (logo + title + study/site + tenant + date), per-page footer
  // (tenant name + Page X of Y), and a last-page platform attribution slip.
  // Implemented on top of jsPDF (vector text for cover/footer, html2canvas
  // slice for the body — see utils/pdfBranding.js).
  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const element = reportRef.current;
      if (!element) {
        showError('Report content not ready');
        return;
      }

      const studyName = study?.study_name || study?.name || 'Study';
      const siteName = site?.name || '';
      const subtitleParts = [studyName];
      if (siteName) subtitleParts.push(siteName);

      await generateBrandedPdf({
        element,
        filename: `GHG_Report_${studyName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        reportTitle: 'GHG Emissions Summary',
        subtitle: subtitleParts.join(' • '),
        tenantName: getCurrentTenantName(),
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const scope1Percent = ghgData ? ((ghgData.scope1.total_tco2e / (ghgData.total_ghg.total_ghg_tco2e + (ghgData.scope3?.total_tco2e || 0))) * 100).toFixed(1) : 0;
  const scope2Percent = ghgData ? ((ghgData.scope2.total_tco2e / (ghgData.total_ghg.total_ghg_tco2e + (ghgData.scope3?.total_tco2e || 0))) * 100).toFixed(1) : 0;
  const scope3Percent = ghgData ? (((ghgData.scope3?.total_tco2e || 0) / (ghgData.total_ghg.total_ghg_tco2e + (ghgData.scope3?.total_tco2e || 0))) * 100).toFixed(1) : 0;
  const totalWithScope3 = ghgData ? (ghgData.total_ghg.total_ghg_tco2e + (ghgData.scope3?.total_tco2e || 0)) : 0;

  return (
    <div className="space-y-6">
      <div ref={reportRef}>
      {/* PDF-only layout overrides. The branded-pdf helper wraps the cloned
          DOM in .branded-pdf-capturing and captures at 900px, where a 3-column
          grid squeezes each card to ~280px and labels wrap mid-row. These
          rules only apply during capture, so the on-screen layout is unchanged. */}
      <style>{`
        .branded-pdf-capturing .ghg-pdf-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
        .branded-pdf-capturing .ghg-pdf-grid > div { page-break-inside: avoid !important; break-inside: avoid !important; }
        .branded-pdf-capturing .ghg-pdf-card-header { flex-wrap: wrap !important; }
      `}</style>
      {/* Total GHG Card */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-300 text-sm mb-1">Total GHG Emissions (Scope 1+2+3)</p>
            <p className="text-4xl font-bold">{totalWithScope3?.toLocaleString() || '0'} <span className="text-xl font-normal text-slate-300">tCO₂e</span></p>
            <p className="text-sm text-slate-400 mt-2">
              Based on {study?.currency === 'SGD' ? 'Singapore EMA' : 'India CEA'} grid emission factors
            </p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-3 h-3 bg-orange-500 rounded-full"></span>
              <span className="text-sm">Scope 1: {scope1Percent}%</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-3 h-3 bg-blue-500 rounded-full"></span>
              <span className="text-sm">Scope 2: {scope2Percent}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-cyan-500 rounded-full"></span>
              <span className="text-sm">Scope 3: {scope3Percent}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scope 1, 2 & 3 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 ghg-pdf-grid">
        {/* Scope 1 Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Flame className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Scope 1 Emissions</h3>
                <p className="text-xs text-gray-500">Direct emissions from fuel combustion</p>
              </div>
            </div>
            <span className="text-2xl font-bold text-orange-600">{ghgData?.scope1?.total_tco2e?.toFixed(1) || '0'}</span>
          </div>
          
          <div className="space-y-3">
            {ghgData?.scope1?.fuel_breakdown && (Array.isArray(ghgData.scope1.fuel_breakdown) ? ghgData.scope1.fuel_breakdown.length > 0 : Object.keys(ghgData.scope1.fuel_breakdown).length > 0) ? (
              Object.entries(ghgData.scope1.fuel_breakdown).map(([key, emissions]) => {
                // Backend returns a list of {fuel_type, equipment, emissions_tco2e}; older paths
                // return a {fuel_type: tco2e} dict — handle both.
                const isObj = typeof emissions === 'object' && emissions !== null;
                const label = isObj
                  ? `${emissions.fuel_type || 'Unknown'}${emissions.equipment ? ` — ${emissions.equipment}` : ''}`
                  : key;
                const value = isObj ? emissions.emissions_tco2e : Number(emissions);
                return (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-gray-600">{label}</span>
                    <span className="font-medium">{value?.toFixed(1) || '0'} tCO₂e</span>
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-gray-400 italic">No fuel data entered yet</div>
            )}
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Info className="w-3 h-3" />
              <span>Formula: Fuel Qty × NCV × EF ÷ 10⁶ (IPCC AR6)</span>
            </div>
          </div>
        </div>

        {/* Scope 2 Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Scope 2 Emissions</h3>
                <p className="text-xs text-gray-500">Indirect emissions from purchased electricity</p>
              </div>
            </div>
            <span className="text-2xl font-bold text-blue-600">{ghgData?.scope2?.total_tco2e?.toFixed(1) || '0'}</span>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Electricity</span>
              <span className="font-medium">{(ghgData?.scope2?.total_kwh / 1000)?.toFixed(0) || '0'} MWh</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Grid Region</span>
              <span className="font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{study?.currency === 'SGD' ? 'Singapore' : 'India'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Grid Emission Factor</span>
              <span className="font-medium">{ghgData?.scope2?.grid_factor || 0.716} kgCO₂/kWh</span>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Info className="w-3 h-3" />
              <span>Formula: kWh × Grid Factor ÷ 1000 ({study?.currency === 'SGD' ? 'EMA' : 'CEA'})</span>
            </div>
          </div>
        </div>

      {/* Scope 3 Card */}
      <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-cyan-500">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Scope 3 Emissions</h3>
              <p className="text-xs text-gray-500">Value chain (screening-grade)</p>
            </div>
          </div>
          <span className="text-2xl font-bold text-cyan-600">
            {ghgData?.scope3?.total_tco2e?.toFixed(1) || '0'}
          </span>
        </div>
        
        <div className="space-y-3">
          {ghgData?.scope3?.categories?.slice(0, 3).map((cat, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-gray-600">{cat.category.substring(0, 25)}...</span>
              <span className="font-medium">{cat.emissions_tco2e.toFixed(1)} tCO₂e</span>
            </div>
          )) || (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">No data yet</span>
                <span className="font-medium">-</span>
              </div>
            </>
          )}
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-xs text-orange-500">
            <AlertCircle className="w-3 h-3" />
            <span>Screening-grade estimates for prioritization</span>
          </div>
        </div>
      </div>

      {/* Grid now 3 cards */}
      </div>

      {/* Resource Summary */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Resource Consumption Summary</h2>
        {(ghgData?.scope2?.total_kwh > 0 || (ghgData?.scope1?.fuel_breakdown && (Array.isArray(ghgData.scope1.fuel_breakdown) ? ghgData.scope1.fuel_breakdown.length > 0 : Object.keys(ghgData.scope1.fuel_breakdown).length > 0))) ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Resource</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Annual Consumption</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Unit</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Emissions (tCO₂e)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {ghgData?.scope2?.total_kwh > 0 && (
              <tr className="bg-white">
                <td className="px-4 py-3 font-medium text-gray-900 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" /> Electricity
                </td>
                <td className="px-4 py-3 text-right">{ghgData.scope2.total_kwh?.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-gray-600">kWh</td>
                <td className="px-4 py-3 text-right">{ghgData.scope2.total_tco2e?.toFixed(1)}</td>
              </tr>
              )}
              {(() => {
                // Aggregate per-SEU rows into one row per (fuel_type, unit) so the
                // Resource Consumption Summary doesn't list "Diesel, PNG, PNG, ...".
                const raw = ghgData?.scope1?.fuel_breakdown;
                if (!raw) return null;
                const rows = Array.isArray(raw)
                  ? raw
                  : Object.entries(raw).map(([k, v]) => ({ fuel_type: k, annual_qty: 0, unit: '', emissions_tco2e: v }));
                const grouped = {};
                for (const r of rows) {
                  const ft = r.fuel_type || 'Unknown';
                  const u = r.unit || '';
                  const key = `${ft}__${u}`;
                  if (!grouped[key]) grouped[key] = { fuel_type: ft, unit: u, annual_qty: 0, emissions_tco2e: 0 };
                  grouped[key].annual_qty += Number(r.annual_qty) || 0;
                  grouped[key].emissions_tco2e += Number(r.emissions_tco2e) || 0;
                }
                return Object.values(grouped).map((fuel, i) => (
                  <tr key={`${fuel.fuel_type}-${fuel.unit}`} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-4 py-3 font-medium text-gray-900 flex items-center gap-2">
                      <Flame className="w-4 h-4 text-orange-500" /> {fuel.fuel_type}
                    </td>
                    <td className="px-4 py-3 text-right">{fuel.annual_qty ? fuel.annual_qty.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '-'}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{fuel.unit || '-'}</td>
                    <td className="px-4 py-3 text-right">{fuel.emissions_tco2e.toFixed(1)}</td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <p className="text-sm">No resource consumption data available yet.</p>
            <p className="text-xs mt-1">Complete Desktop Study and SEU Identification to populate this section.</p>
          </div>
        )}
      </div>

      {/* Emission Factor Reference */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
          <Info className="w-5 h-5" /> Emission Factor Reference
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-blue-800">Grid Emission Factor (Scope 2)</p>
            <p className="text-blue-700">
              {study?.currency === 'SGD' 
                ? 'Singapore EMA: 0.408 kgCO₂/kWh (2023)' 
                : 'India CEA Baseline: 0.716 kgCO₂/kWh (2023)'}
            </p>
          </div>
          <div>
            <p className="font-medium text-blue-800">GWP Values (IPCC AR6)</p>
            <p className="text-blue-700">CO₂: 1 | CH₄: 28 | N₂O: 265</p>
          </div>
        </div>
        <p className="text-xs text-blue-600 mt-3">
          * Regional emission factors are auto-selected based on the study&apos;s currency (INR = India, SGD = Singapore). No manual selection required.
        </p>
      </div>
      </div>

      {/* Export Options */}
      <div className="flex justify-end gap-3">
        <button 
          onClick={exportToExcel}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
        >
          <Download className="w-4 h-4" />
          Export to Excel
        </button>
        <button 
          onClick={downloadPDF}
          disabled={downloading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          {downloading ? 'Generating...' : 'Download GHG Report (PDF)'}
        </button>
      </div>
    </div>
  );
}

// ==================== FINAL REPORT TAB ====================
function FinalReportTab({ study }) {
  const [reportStatus] = useState('draft');
  const [expandedSections, setExpandedSections] = useState({
    structure: true,
    ai: false,
    export: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const reportSections = [
    { id: 'exec', title: 'Executive Summary', status: 'pending', description: 'Key findings, recommendations, and ROI summary' },
    { id: 'baseline', title: 'Energy Baseline', status: 'pending', description: 'Current energy consumption patterns and benchmarks' },
    { id: 'seu', title: 'SEU Analysis', status: 'complete', description: 'Significant Energy Users identification and analysis' },
    { id: 'ghg', title: 'GHG Inventory', status: 'complete', description: 'Scope 1 & 2 emissions calculation and breakdown' },
    { id: 'opportunities', title: 'Opportunities', status: 'pending', description: 'Energy saving opportunities with financial analysis' },
    { id: 'roadmap', title: 'Implementation Roadmap', status: 'pending', description: 'Phased action plan with timelines and responsibilities' },
    { id: 'appendix', title: 'Appendices', status: 'pending', description: 'Raw data, calculations, and supporting documents' },
  ];

  return (
    <div className="space-y-6">
      {/* Report Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1">ERS Final Report</h2>
            <p className="text-indigo-200">{study?.study_name || 'Energy Audit 2024'}</p>
            <p className="text-sm text-indigo-300 mt-2">Site: Test Manufacturing Plant • Period: FY 2024-25</p>
          </div>
          <div className="text-right">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              reportStatus === 'draft' ? 'bg-amber-100 text-amber-800' :
              reportStatus === 'review' ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>
              {reportStatus === 'draft' ? 'Draft' : reportStatus === 'review' ? 'Under Review' : 'Final'}
            </span>
            <p className="text-xs text-indigo-300 mt-2">Version 1.0</p>
          </div>
        </div>
      </div>

      {/* Report Progress */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">Report Completion</h3>
          <span className="text-sm text-gray-600">2 of 7 sections complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-3 rounded-full" style={{ width: '28%' }}></div>
        </div>
      </div>

      {/* Report Structure */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => toggleSection('structure')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Report Structure</h2>
          </div>
          {expandedSections.structure ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
        </button>

        {expandedSections.structure && (
          <div className="p-6">
            <div className="space-y-3">
              {reportSections.map((section, idx) => (
                <div key={section.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <span className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                      {idx + 1}
                    </span>
                    <div>
                      <h4 className="font-semibold text-gray-900">{section.title}</h4>
                      <p className="text-xs text-gray-500">{section.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      section.status === 'complete' ? 'bg-green-100 text-green-700' :
                      section.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {section.status === 'complete' ? 'Complete' : section.status === 'in_progress' ? 'In Progress' : 'Pending'}
                    </span>
                    <button className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium">
                      {section.status === 'complete' ? 'View' : 'Edit'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Report Generation */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-md overflow-hidden border border-purple-200">
        <button
          onClick={() => toggleSection('ai')}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-purple-100/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-purple-900">AI Report Generation</h2>
            <span className="px-2 py-1 bg-purple-200 text-purple-700 rounded-full text-xs font-medium">Powered by GPT</span>
          </div>
          {expandedSections.ai ? <ChevronDown className="w-5 h-5 text-purple-500" /> : <ChevronRight className="w-5 h-5 text-purple-500" />}
        </button>

        {expandedSections.ai && (
          <div className="p-6 space-y-4">
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <h4 className="font-semibold text-gray-900 mb-2">Generate Executive Summary</h4>
              <p className="text-sm text-gray-600 mb-4">
                AI will analyze your SEU data, GHG emissions, and identified opportunities to generate a comprehensive executive summary.
              </p>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Generate Summary
              </button>
            </div>
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <h4 className="font-semibold text-gray-900 mb-2">Generate Recommendations</h4>
              <p className="text-sm text-gray-600 mb-4">
                Get AI-powered recommendations based on your energy audit findings, industry benchmarks, and best practices.
              </p>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Generate Recommendations
              </button>
            </div>
            <div className="text-xs text-purple-600 flex items-center gap-2">
              <Info className="w-3 h-3" />
              AI outputs are editable. All generated content includes data source references for traceability.
            </div>
          </div>
        )}
      </div>

      {/* Export Options */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => toggleSection('export')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-bold text-gray-900">Export & Share</h2>
          </div>
          {expandedSections.export ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
        </button>

        {expandedSections.export && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors flex flex-col items-center gap-2">
                <FileDown className="w-8 h-8 text-red-500" />
                <span className="font-medium text-gray-900">Export PDF</span>
                <span className="text-xs text-gray-500">Professional formatted report</span>
              </button>
              <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors flex flex-col items-center gap-2">
                <FileText className="w-8 h-8 text-blue-500" />
                <span className="font-medium text-gray-900">Export Word</span>
                <span className="text-xs text-gray-500">Editable DOCX format</span>
              </button>
              <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors flex flex-col items-center gap-2">
                <Download className="w-8 h-8 text-green-500" />
                <span className="font-medium text-gray-900">Export Excel</span>
                <span className="text-xs text-gray-500">Data tables and calculations</span>
              </button>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 flex items-center justify-center gap-2">
                <FileCheck className="w-5 h-5" />
                Finalize & Publish Report
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Version History */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <h3 className="font-bold text-gray-900 mb-3">Version History</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">v1.0 (Current)</span>
            <span className="text-gray-500">Draft - Created Dec 11, 2025</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          All changes are tracked with user ID and timestamp for audit compliance (CBAM, SGX, Assurance).
        </p>
      </div>
    </div>
  );
}

export default ERSMain;
